<template>
  <div
    ref="serviceNode"
    class="flex w-full cursor-pointer rounded-sm bg-white shadow-sm"
    data-cy="service-node"
    :class="{
      'cursor-pointer': isDraggable,
      'cursor-not-allowed': !isDraggable,
    }"
    :draggable="isDraggable"
    @dragstart="handleDragStart"
    @dragend="handleDragEnd"
  >
    <div
      class="flex items-center justify-center rounded-l p-3"
      :class="{
        'handle-blue': isDragging,
      }"
    >
      <icon-base
        :icon="IconDragHandle"
        width="12"
        height="20"
        :icon-color="handleIconColor"
      />
    </div>
    <service-info-display :model="model" :type="type" />
  </div>
</template>
<script setup lang="ts">
import { computed, getCurrentInstance, ref } from "vue";

import {
  DO_DRAGGABLE_DEFAULT,
  DO_TYPE_DATA_SERVICE,
  UTIL_COLORS,
} from "@/helpers/constants";
import { ORCHESTRATION_DRAG_VAL_CHANGED } from "@/helpers/constants/events";
import { useDoActiveTemplateStatus } from "@/hooks/orchestration";

import IconDragHandle from "@/components/icons/IconDragHandle.vue";
import ServiceInfoDisplay from "@/views/profile/components/dataOrchestration/configPanel/ServiceInfoDisplay.vue";

import type { IDataService } from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IDataService>,
    default: null,
  },
  type: {
    type: String,
    default: "",
  },
});

const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const emitter =
  getCurrentInstance()?.appContext.config.globalProperties.emitter;

const serviceNode = ref<HTMLElement | null>(null);

const isDragging = ref(false);

const isDraggable = computed(() => !isActiveTemplatePublished.value);

const handleIconColor = computed(() =>
  isDragging.value ? "white" : UTIL_COLORS.DEFAULT
);

const handleDragStart = (e: DragEvent) => {
  // Setting high z-index for case when part of service node is behind other section
  serviceNode.value?.classList.add("z-100");
  isDragging.value = true;
  e.dataTransfer?.setData("text", JSON.stringify(props.model));
  emitter.emit(ORCHESTRATION_DRAG_VAL_CHANGED, {
    isDragging: true,
    type: DO_TYPE_DATA_SERVICE,
    serviceData: props.model,
  });
};

const handleDragEnd = () => {
  // Removing z-index when dragging has stopped
  serviceNode.value?.classList.remove("z-100");
  emitter.emit(ORCHESTRATION_DRAG_VAL_CHANGED, DO_DRAGGABLE_DEFAULT);
  isDragging.value = false;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.handle-blue {
  background-color: #3769d6;
}
</style>
